<script lang="ts" setup>
  import type { Entry } from 'contentful'
  import type { JsonObject } from 'contentful/node_modules/type-fest'
  import { createRestaurantDefaultContent } from '@/constants/restaurantDetails'
  import { DEFAULT_SEO_META } from '@/constants/seo'
  import { GRILLD_URL } from '@/constants/url'
  import { OrderTypeEnum, type Restaurant } from '@/types/api/restaurant'
  import type { TypePageSkeleton } from '@/types/contentful'
  import type { ImageProviderSource, MediaRatios } from '@/types/media'
  import { getRestaurantMetaDescription, getRestaurantTitle } from '@/utils/seo'
  import { SITE_ENVIRONMENTS } from '~/constants/siteEnvironment'
  import { gaEventGroupBookingClicked, gaEventCateringClicked } from '@/composables/analytics'

  const slug = useRoute().params.slug as string[]
  const slugString: string = slug.join('/').toLowerCase()

  const $config = useRuntimeConfig()
  const useRestaurantPage = $config.public.siteEnvironment === SITE_ENVIRONMENTS.AUSTRALIA

  // Australian restaurant page mode

  const restaurantResult = await useAsyncData(async () => {
    if (!useRestaurantPage) {
      return null
    }

    const res = await $fetch<{
      restaurant: Restaurant
      page: Entry<TypePageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string> | undefined
      restaurantPage: Entry<TypePageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string> | undefined
    }>(`/api/restaurants/${slugString}`)

    const defaultContent = createRestaurantDefaultContent({
      catering: res.restaurant.orderTypes.includes(OrderTypeEnum.CATERING),
      restaurantId: res.restaurant.id,
      obeeId: res.restaurant.obeeId
    })

    return {
      restaurant: res.restaurant,
      page: res.page,
      restaurantPage: res.restaurantPage,
      defaultContent
    }
  })

  const restaurantData = restaurantResult.data
  const restaurantError = restaurantResult.error

  if (useRestaurantPage) {
    if (
      !restaurantData.value?.restaurant ||
      !restaurantData.value?.page ||
      !restaurantData.value?.defaultContent ||
      restaurantError.value
    ) {
      throw createError({
        ...restaurantError.value,
        statusCode: 404,
        statusMessage: 'No Restaurant or Default content. Page not found',
        fatal: true
      })
    }

    if (restaurantData.value.page.fields.redirect) {
      navigateTo(restaurantData.value.page.fields.redirect, {
        replace: true,
        redirectCode: 301
      })
    }

    useGrilldSeoHead({
      pageTitle: getRestaurantTitle(restaurantData.value!.restaurant.name),
      canonicalUrl: restaurantData.value?.restaurant.slug
        ? GRILLD_URL + '/restaurants/' + restaurantData.value?.restaurant.slug
        : undefined,
      jsonLinkingData: [
        restaurantData.value?.page?.fields?.seo?.fields.jsonLinkingData as JsonObject,
        dynamicLocalBusinessSchema(restaurantData.value.restaurant, slugString) as JsonObject
      ]
    })

    useServerSeoMeta({
      ogTitle: () => getRestaurantTitle(restaurantData.value!.restaurant.name),
      description: () => getRestaurantMetaDescription(restaurantData.value?.restaurant.name),
      ogDescription: () => getRestaurantMetaDescription(restaurantData.value!.restaurant.name),
      ogSiteName: DEFAULT_SEO_META.ogSiteName,
      ogImage: () =>
        restaurantData.value?.restaurant.imageUrl ?? GRILLD_URL + '/assets/restaurant/ogImage-fallback-restaurant.jpg',
      ogImageAlt: restaurantData.value?.restaurant.name || DEFAULT_SEO_META.ogImageAlt,
      ogLocale: DEFAULT_SEO_META.ogLocale,
      ogType: DEFAULT_SEO_META.ogType,
      ogUrl: () =>
        restaurantData.value?.restaurant.slug
          ? GRILLD_URL + '/restaurants/' + restaurantData.value.restaurant.slug
          : undefined
    })
  }

  const differentiatorImageRatios: MediaRatios<'M'> = {
    M: {
      desktop: {
        numerator: 3,
        denominator: 1
      },
      mobile: {
        numerator: 2,
        denominator: 1
      }
    }
  }

  const differentiatorImage = computed(() => {
    const cmsImageUrl =
      restaurantData.value?.restaurant.differentiatorImageUrl || restaurantData.value?.restaurant.imageUrl

    return {
      provider: 'ipx' as ImageProviderSource,
      alt: restaurantData.value?.restaurant.name,
      src: cmsImageUrl || '/assets/restaurant/3_1-differentiator.webp'
    }
  })

  // regular slug page mode

  const slugResult = await useAsyncData(async () => {
    if (useRestaurantPage) {
      return null
    }

    const result = await useFetch<Entry<TypePageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>>(
      `/api/cms/page/${slugString}`
    )

    return result.data.value
  })

  const slugData = slugResult.data
  const slugError = slugResult.error

  if (!useRestaurantPage) {
    if (!slugData.value || slugError.value) {
      throw createError({
        statusCode: 404,
        statusMessage: `Route at '${slugString}' not found`,
        fatal: true
      })
    }

    if (slugData.value.fields.redirect) {
      navigateTo(slugData.value.fields.redirect, {
        replace: true,
        redirectCode: 301
      })
    }

    useGrilldServerSeoMeta(slugData.value?.fields.seo)
    useGrilldSeoHead({
      pageTitle: slugData.value?.fields.seo?.fields.metaTitle ?? undefined,
      canonicalUrl: slugData.value?.fields.seo?.fields.canonicalUrl ?? undefined,
      jsonLinkingData: slugData.value?.fields.seo?.fields.jsonLinkingData ?? undefined
    })
  }

  onMounted(() => {
    const restaurantIdState = useState('restaurantId')
    restaurantIdState.value = restaurantData.value?.restaurant.id ?? null
  })
  onUnmounted(() => {
    // Clear the restaurantId state when the component is unmounted
    useState('restaurantId').value = null
  })
</script>

<template>
  <!-- Australia restaurant page mode -->
  <div v-if="useRestaurantPage && restaurantData" class="restaurant-page-container">
    <CRestaurantDetailsHeader v-if="restaurantData.restaurant" :restaurant="restaurantData.restaurant" />
    <COrderNowBanner :restaurant-id="restaurantData.restaurant.id" />
    <section class="differentiator-image module-paddings">
      <CImageBlock
        :alt="differentiatorImage.alt"
        :provider="differentiatorImage.provider"
        :ratios="differentiatorImageRatios"
        :src="differentiatorImage.src"
        loading="eager"
        ratio="M"
      />
    </section>

    <MPageModules
      v-if="restaurantData.restaurantPage"
      :eager-load-module-amount="2"
      :page-data="restaurantData.restaurantPage"
    />

    <MPageModules v-if="restaurantData.page" :eager-load-module-amount="2" :page-data="restaurantData.page" />

    <CSpacer size="S" />

    <template v-if="restaurantData.defaultContent.catering">
      <MImageAndText
        :data="restaurantData.defaultContent.catering"
        provider="ipx"
        @primary-click="gaEventCateringClicked('cta')"
      />
      <CSpacer size="S" />
    </template>

    <template v-if="restaurantData.defaultContent.groupBooking">
      <MImageAndText
        :data="restaurantData.defaultContent.groupBooking"
        provider="ipx"
        @primary-click="gaEventGroupBookingClicked('cta')"
      />
      <CSpacer size="M" />
    </template>

    <div class="grilld-hand-heart">
      <icon-grilld-hand-heart alt="" />
    </div>

    <MTextAndButtons :data="restaurantData.defaultContent.localMattersText" />
    <MTextAndButtons :data="restaurantData.defaultContent.learnMoreButton" />

    <template v-if="restaurantData.restaurant.localMatters?.length">
      <CDivider color="var(--color-base-red)" />
      <CSpacer size="S" />

      <section class="local-matters module-paddings">
        <div v-for="item in restaurantData.restaurant.localMatters" :key="item.name" class="local-matters-card">
          <span class="body-large-strong">{{ item.name }}</span>
          <p class="body">{{ item.description }}</p>
        </div>
      </section>
    </template>

    <CSpacer size="L" />
    <MImage :data="restaurantData.defaultContent.localMattersAnimation" />
    <CFindAnotherGrilldBanner />
  </div>

  <!-- regular slug page mode -->
  <MPageModules v-if="!useRestaurantPage && slugData" :page-data="slugData" />
</template>

<style lang="scss" scoped>
  .restaurant-page-container {
    background-color: var(--color-base-white);

    .differentiator-image {
      background-color: var(--color-secondary-paper-light);

      :deep(.c-image-block) {
        img {
          border-radius: 8px;

          @include screenMdAndUp {
            border-radius: 12px;
          }
        }
      }
    }

    .grilld-hand-heart {
      display: flex;
      align-items: center;
      justify-content: center;

      :deep(svg) {
        width: 105px;
        height: 80px;
      }

      @include screenMdAndUp {
        :deep(svg) {
          width: 158px;
          height: 120px;
        }
      }
    }

    .local-matters {
      display: flex;
      flex-direction: column;
      gap: 32px;

      @include screenLgAndUp {
        flex-direction: row;
      }

      .local-matters-card {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 16px;
      }
    }
  }
</style>
